import { useCallback } from 'react';
import styled from 'styled-components';
import {
  isDev,
  Tooltip,
  useIsMsalAuth,
} from '@equifinance/shared';
import { useAccountContext, useChecklistContext } from '../../contexts';
import { Stages } from '../../types/Stages';

export enum ProgressTrackerStatus {
  Active,
  Inactive,
  Checked,
}

type statusType = ProgressTrackerStatus.Active | ProgressTrackerStatus.Inactive | ProgressTrackerStatus.Checked;

const MainContainer = styled.div`${({ theme }) => `
  width: 100%;
  height: ${theme.fontSize[11]};
  position: relative;
  z-index:4;
  margin: auto;
  display: grid;
  background-color: ${theme.colours.neutral[9]};
  box-shadow: ${theme.boxShadow[2]};
`}`;

const StepContainer = styled.div`${({ theme }) => `
  position:absolute;
  top: 50%;
  transform: translate(0, -50%);
  display: flex;
  justify-content: space-evenly;
  margin-left: ${theme.padding[7]};
`}`;

const StepWrapper = styled.div`
  display: inline-flex;
  z-index: 1;
`;

const Mark = styled.span`${({ theme }) => `
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[3]};
  line-height: ${theme.fontSize[7]};
  border-radius: 50%;
  width: ${theme.fontSize[5]};
  height: ${theme.fontSize[5]};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  letter-spacing: 0.2px;
  cursor: default;
  box-sizing: border-box;
`}`;

const ProgressMark = styled(Mark)<{ status: statusType}>`${({ theme, status }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${status === ProgressTrackerStatus.Active ? theme.fontWeight.bolder : theme.fontWeight.bold};
  color: ${
  ({
    [ProgressTrackerStatus.Active]: theme.colours.primary[1],
    [ProgressTrackerStatus.Inactive]: theme.colours.neutral[6],
    [ProgressTrackerStatus.Checked]: theme.colours.neutral[10],
  } as Record<string, string>
  )[status]};
  background-color: ${
  ({
    [ProgressTrackerStatus.Active]: theme.colours.secondary[3],
    [ProgressTrackerStatus.Inactive]: theme.colours.neutral[9],
    [ProgressTrackerStatus.Checked]: theme.colours.neutral[5],
  } as Record<string, string>
  )[status]};
  border: ${
  ({
    [ProgressTrackerStatus.Active]: `${theme.borderWidth[1]} solid ${theme.colours.secondary[3]}`,
    [ProgressTrackerStatus.Inactive]: `${theme.borderWidth[1]} solid ${theme.colours.neutral[6]}`,
    [ProgressTrackerStatus.Checked]: `${theme.borderWidth[1]} solid ${theme.colours.neutral[5]}`,
  } as Record<string, string>
  )[status]};
`}`;

const ProgressLabel = styled.div<{ status: statusType}>`${({ theme, status }) => `
  padding-left: ${theme.padding[2]};
  font-size: ${theme.fontSize[1]};
  font-family: 'Open Sans';
  line-height: ${theme.fontSize[3]};
  cursor: default;
  height: ${theme.fontSize[9]};
  display: flex;
  align-items: center;
  color: ${
  ({
    [ProgressTrackerStatus.Active]: theme.colours.primary[1],
    [ProgressTrackerStatus.Inactive]: theme.colours.neutral[6],
    [ProgressTrackerStatus.Checked]: theme.colours.neutral[4],
  } as Record<string, string>
  )[status]};
  font-weight: ${
  ({
    [ProgressTrackerStatus.Active]: theme.fontWeight.bolder,
    [ProgressTrackerStatus.Inactive]: theme.fontWeight.bold,
    [ProgressTrackerStatus.Checked]: theme.fontWeight.bold,
  } as Record<string, string>
  )[status]};
`}`;

const ProgressTrackerArrow = styled.i` ${({ theme }) => `
  padding:0 ${theme.padding[6]};
  color: ${theme.colours.neutral[5]};
  font-size: ${theme.fontSize[3]};
`}`;

const ProgressTrackerVector = styled.i` ${({ theme }) => `
  position: absolute;
  color: ${theme.colours.secondary[3]};
  font-size: ${theme.fontSize[8]};
  margin-top: calc(${theme.padding[5]} - ${theme.borderWidth[1]});
  margin-left: -6px;
`}`;

const ProgressTrackerWrapper = styled.div`
  display: inline-flex;
  flex-shrink:0;
  align-items: center;
`;

type StepType = {
  type: Stages,
  label: string,
  guideMessage: string,
  completedMessage: string,
};

const stageToIndex = {
  [Stages.CreditDecision]: 0,
  [Stages.Affordability]: 1,
  [Stages.Packaging]: 2,
  [Stages.AtLender]: 3,
  [Stages.Offer]: 4,
  [Stages.Completion]: 5,
};

export default function AccountStage() {
  const { account, updateAccount } = useAccountContext();
  const { updateChecklist } = useChecklistContext();
  const isMsalAuth = useIsMsalAuth();

  const { stage, status } = account;

  const completed = stage === Stages.Completion && status === 'Complete';

  const steps: StepType[] = [
    {
      type: Stages.CreditDecision,
      label: 'Credit Decision',
      guideMessage: 'Complete Application Details to obtain an offer based<br>on Credit data.',
      completedMessage: 'Credit Decision has been<br/>successfully completed.',
    },
    {
      type: Stages.Affordability,
      label: 'Affordability',
      guideMessage: 'Provide high level income and expenditure<br>information and additional credit information to get a<br>decision based on Credit and Affordability.',
      completedMessage: 'Affordability has been<br>successfully completed.',
    },
    {
      type: Stages.Packaging,
      label: 'Packaging',
      guideMessage: 'Complete final application data and provide<br>necessary documents to allow submission to Lender.',
      completedMessage: 'Packaging has been<br>successfully completed.',
    },
    {
      type: Stages.AtLender,
      label: 'Underwriting',
      guideMessage: 'Once the application has been submitted, it will be<br>underwritten.',
      completedMessage: 'Underwriting completed.',
    },
    {
      type: Stages.Offer,
      label: 'Offer',
      guideMessage: 'Complete security call<br>and send offer.',
      completedMessage: 'Offer is successfully sent.',
    },
    {
      type: Stages.Completion,
      label: 'Completion',
      guideMessage: 'Once post-offer activities are completed, the<br>application will be sent to Phoebus.',
      completedMessage: 'Application has been sent to Phoebus.',
    },
  ];

  const getStatusAndTooltipMessage = (selectedStep: number, stepIndex: number, step: StepType) => {
    const defaultStatusAndTooltip = {
      status: ProgressTrackerStatus.Inactive,
      tooltipMessage: step.guideMessage,
    };

    if (selectedStep > stepIndex || completed) {
      defaultStatusAndTooltip.status = ProgressTrackerStatus.Checked;
      defaultStatusAndTooltip.tooltipMessage = step.completedMessage;
    } else if (selectedStep === stepIndex) {
      defaultStatusAndTooltip.status = ProgressTrackerStatus.Active;
    }

    return defaultStatusAndTooltip;
  };

  const onClickProgressTracker = async (stage: Stages) => {
    if (isMsalAuth) {
      await updateChecklist('stage', stage);
    }

    if (isDev()) {
      await updateAccount('stage', stage);
    }
  };

  const renderStep = useCallback((stepIndex: number, step: StepType) => {
    const { status, tooltipMessage } = getStatusAndTooltipMessage(stageToIndex[stage], stepIndex, step);

    return (
      <>
        <ProgressTrackerWrapper onClick={() => onClickProgressTracker(step.type)}>
          <ProgressMark status={status}>{stepIndex + 1}</ProgressMark>
          <ProgressLabel id={`${step.label}-tooltip`} status={status}>{step.label}</ProgressLabel>
          <Tooltip
            id={`${step.label}-tooltip`}
            html={tooltipMessage}
            place="bottom"
          />
          {(stepIndex !== steps.length - 1) && (
            <ProgressTrackerArrow className="mi-chevron-right" />
          )}
        </ProgressTrackerWrapper>
        {stageToIndex[stage] === stepIndex && !completed && (
          <ProgressTrackerVector className="mi-caret-up" />
        )}
      </>
    );
  }, [stage, completed]);

  return (
    <MainContainer>
      <StepContainer>
        {steps?.map((step, stepIndex) => (
          <StepWrapper key={step.label}>
            {renderStep(stepIndex, step)}
          </StepWrapper>
        ))}
      </StepContainer>
    </MainContainer>
  );
}
